<template>
    <v-dialog
        v-model="showDialog"
        persistent
        width="800"
        scrollable
        :fullscreen="this.$vuetify.breakpoint.xsOnly"
    >
        <v-card>
            <v-card-title style="font-size: 20px;">
                View Image
            </v-card-title>
            <v-divider />
            <v-card-text class="pb-0 mt-4 previewImageCaption">
                {{ getImage.caption }}
                <v-btn
                    v-if="!imgLoading && !this.$vuetify.breakpoint.xs"
                    class="text-subtitle-2 float-right"
                    depressed
                    color="primary"
                    :loading="downloading"
                    @click="downloadItem"
                >
                    <v-icon>mdi-download-outline</v-icon>
                    Download
                </v-btn>
            </v-card-text>
            <v-card-text 
                ref="cardText"
                class="pt-3"
                :class="{
                    'mt-2': isPdf(getImage.file_type),
                    'heightVhMob': !isImage(getImage.file_type) && this.$vuetify.breakpoint.xs,
                    'heightVhWeb': isText(getImage.file_type) && !this.$vuetify.breakpoint.xs
                }"
            >
                <v-layout
                    v-if="imgLoading"
                    align-center
                    justify-center
                    column
                    fill-height
                    style="height:60vh"
                >
                    <v-flex
                        row
                        align-center
                    >
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            :size="40"
                            class=""
                        />
                    </v-flex>
                </v-layout>
                <v-img
                    v-if="!imgLoading && isImage(getImage.file_type)"
                    alt="image"
                    max-width="800"
                    :src="imgUrl"
                    @load="imgLoading = false"
                />
                <span
                    v-if="!imgLoading && isText(getImage.file_type)"
                    style="font-size: 14px; display: block; word-wrap: break-word; white-space: pre-wrap;"
                >
                    {{ imgData }}
                </span>
                <div
                    v-if="!imgLoading && isPdf(getImage.file_type)"
                >
                    <span>PDF previews are not available, please download the pdf.</span>
                </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="v-card-action">
                <v-spacer />
                <v-btn
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    outlined
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="cancel"
                >
                    Close
                </v-btn>
                <v-btn
                    v-if="!imgLoading && this.$vuetify.breakpoint.xs"
                    style="text-transform: none !important"
                    class="text-subtitle-2"
                    depressed
                    color="primary"
                    :width="this.$vuetify.breakpoint.xs ? '140px' : '76px'"
                    @click="downloadItem"
                >
                    <v-icon>mdi-download-outline</v-icon>
                    Download
                </v-btn>
                <v-spacer v-if="this.$vuetify.breakpoint.xs" />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import webtmsBackend from '../services/webtmsBackend';
import serviceWorkerManager from '../services/serviceWorkerManager'; 

export default {
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        previewImage: {
            type: Object,
            default: () => ({})
        },
    },
    data() {
        return {
            imgData: '',
            imgLoading: false,
            imgUrl: '',
            downloading: false
        };
    },
    computed: {
        showDialog: {
            get() {
                return this.show;                
            },
            set() {
                this.imgLoading = false;
                this.downloading = false;
                this.$emit('closeImagePreviewDialog');
            },
        },
        getTicketDetails() {
            return this.$store.getters.ticketDetails;
        },
        getUser() {
            return this.$store.getters.user;
        },
        getImage() {
            return this.previewImage;
        }
    },
    watch: {
        showDialog(show) {
            if(!show) {
                this.imageUrl = '';
            }
        },
        async getImage(image) {
            if(image.file_url) {                
                this.imgLoading = true;
                if(image.offline) {
                    if(this.isText(image.file_type)) {
                        this.imgData = await serviceWorkerManager.getOfflineImage(image.rowid);
                    }
                    this.imgLoading = false;
                    this.imgUrl = `${window.location.origin}/service-worker/offline-image-data?rowid=${image.rowid}`;
                } else {
                    if(this.isText(image.file_type)) {
                        let imageData = await webtmsBackend.getImageData(image.file_url, image.file_type);
                        this.imgData = imageData;
                    }
                    this.imgLoading = false;
                    this.imgUrl = image.file_url;
                }
            }
        }
    },
    methods: {
        cancel() {
            this.showDialog=false;
        },
        async downloadItem() {
            const link = document.createElement('a');
            if (this.isText(this.getImage.file_type)) {
                let textToSaveAsBlob = new Blob([this.imgData], {type:'text/plain'});
                let textToSaveAsURL = window.URL.createObjectURL(textToSaveAsBlob);
                link.href = textToSaveAsURL;
            } else {
                this.downloading = true;
                link.href = await webtmsBackend.getImageData(this.imgUrl, this.getFileTypeAbbreviation(this.getImage.file_type));
                this.downloading = false;
            }
            link.download = this.getImage.caption + '.' + this.getFileTypeAbbreviation(this.getImage.file_type);
            link.click();
            URL.revokeObjectURL(link.href);
        },
        isImage(fileType) {
            return [
                'gif',
                'jpg',
                'jpeg',
                'png',
                'tiff',
                'tif',
                'heic',
                'image/gif',
                'image/jpg',
                'image/jpeg',
                'image/png',
                'image/tiff',
                'image/heic'
            ].includes(fileType);
        },
        isText(fileType) {
            return [
                'txt',
                'text/plain'
            ].includes(fileType);
        },
        isPdf(fileType) {
            return [
                'pdf',
                'application/pdf'
            ].includes(fileType);
        },
        getFileTypeAbbreviation(fileType) {
            if(this.isImage(fileType)) {
                return fileType.replace('image/','');
            }
            if(this.isText(fileType)) {
                return 'txt';
            }
            if(this.isPdf(fileType)) {
                return fileType.replace('application/','');
            }
        }
    }
};
</script>
<style>
    .previewImageCaption {
        font-size: 16px;
        font-weight: 500;
    }
    .v-card-action {
        padding: 8px 24px !important;
    }
    .previewImageCaption button {
        text-transform: none;
    }
    .heightVhMob {
        height: 80vh;
    }
    .heightVhWeb {
        height: 68vh;
    }
</style>